import type { NodeLatest } from './iotApi';
import { canonicalNumber, getPressure } from './liveSensorMetrics';
import { FRESHNESS_MS, type TelemetryPoint } from './operationalOverview';

export const IRRIGATION_PRESSURE_THRESHOLD_BAR = 0.3;
export type PressureState = 'active' | 'stopped' | 'unknown';
export interface IrrigationInterval {
  startedAt: number;
  endedAt: number;
  durationMs: number;
  minimum: boolean;
  estimated: boolean;
}
export interface PressureDuration {
  state: PressureState;
  pressure: number | null;
  since: number | null;
  minimum: boolean;
  estimated?: boolean;
  lastIrrigation?: IrrigationInterval;
}
const unknown: PressureDuration = { state: 'unknown', pressure: null, since: null, minimum: true };
const valid = (value: number | undefined): value is number => value !== undefined && Number.isFinite(value) && value >= 0;
const classify = (value: number): Exclude<PressureState, 'unknown'> => {
  if (value > IRRIGATION_PRESSURE_THRESHOLD_BAR) return 'active';
  return 'stopped';
};

/** Supervision cadence only: never changes command freshness guards. */
export function pressureFreshnessMs(latest: NodeLatest | null | undefined, history: TelemetryPoint[]): number {
  const configured = canonicalNumber(latest?.measures, 'reporting_interval', 'second') ?? latest?.measures?.reporting_interval_s;
  if (typeof configured === 'number' && Number.isFinite(configured) && configured > 0) {
    return Math.max(FRESHNESS_MS, configured * 2000);
  }
  // Older decoders may omit the configured interval. Require repeated evidence
  // before inferring cadence; a single lost packet must not redefine it.
  const cutoff = Date.parse(latest?.ts ?? new Date().toISOString());
  const times = Array.from(new Set(history.filter(p => valid(getPressure(p.measures ?? undefined)))
    .map(p => Date.parse(p.ts ?? '')).filter(t => Number.isFinite(t) && t <= cutoff))).sort((a, b) => a - b).slice(-13);
  const gaps = times.slice(1).map((t, i) => t - times[i]).filter(g => g > 0).sort((a, b) => a - b);
  return gaps.length >= 6 ? Math.max(FRESHNESS_MS, 2 * gaps[Math.floor((gaps.length - 1) / 2)]) : FRESHNESS_MS;
}

/** Two distinct consecutive valid readings confirm a transition, dated at the first.
 * Gaps preserve observed boundaries but mark duration estimated; an isolated dip
 * never creates a completed irrigation interval.
 */
export function pressureDuration(latest: NodeLatest | null | undefined, history: TelemetryPoint[], now: number): PressureDuration {
  const freshnessMs = pressureFreshnessMs(latest, history);
  const ts = Date.parse(latest?.ts ?? '');
  const pressure = getPressure(latest?.measures);
  const validTime = Number.isFinite(ts) && ts <= now;
  const cutoff = validTime ? ts : now;
  let currentKnown = validTime && now - ts < freshnessMs && valid(pressure);
  const historicalTolerance = new Map<number, number>();
  const byTimestamp = new Map<number, number | undefined>();
  for (const point of history) {
    const sampleTime = Date.parse(point.ts ?? '');
    if (!Number.isFinite(sampleTime) || sampleTime > cutoff) continue;
    const interval = canonicalNumber(point.measures ?? undefined, 'reporting_interval', 'second') ?? point.measures?.reporting_interval_s;
    if (typeof interval === 'number' && Number.isFinite(interval) && interval > 0) {
      const tolerance = Math.max(FRESHNESS_MS, interval * 2000);
      historicalTolerance.set(sampleTime, Math.min(historicalTolerance.get(sampleTime) ?? tolerance, tolerance));
    }
    const value = getPressure(point.measures ?? undefined);
    byTimestamp.set(sampleTime, byTimestamp.has(sampleTime) && byTimestamp.get(sampleTime) !== value ? undefined : value);
  }
  // Duplicate readings cannot confirm a transition; contradictory latest is unknown.
  if (validTime && byTimestamp.has(ts) && byTimestamp.get(ts) !== pressure) {
    byTimestamp.set(ts, undefined);
    currentKnown = false;
  }
  // An unknown latest reading cannot confirm a historical cycle by itself.
  if (currentKnown) byTimestamp.set(ts, pressure);
  const points = Array.from(byTimestamp, ([time, value]) => ({ time, value })).sort((a, b) => a.time - b.time);
  let state: Exclude<PressureState, 'unknown'> | undefined;
  let since = ts;
  let minimum = true;
  let confirmed = false;
  let estimated = false;
  let previousTime: number | undefined;
  let previousTolerance = FRESHNESS_MS;
  let pending: { time: number; estimated: boolean } | undefined;
  let lastIrrigation: IrrigationInterval | undefined;
  for (const point of points) {
    if (!valid(point.value)) {
      estimated = true;
      if (pending) pending.estimated = true;
      continue;
    }
    const gap = previousTime !== undefined && point.time - previousTime >= previousTolerance;
    if (gap) {
      estimated = true;
      if (pending) pending.estimated = true;
    }
    previousTime = point.time;
    previousTolerance = historicalTolerance.get(point.time) ?? FRESHNESS_MS;
    const observed = classify(point.value);
    if (state === undefined) {
      state = observed;
      since = point.time;
      continue;
    }
    if (observed === state) {
      if (!pending) confirmed = true;
      if (pending) estimated = true;
      pending = undefined;
      continue;
    }
    if (!pending) {
      pending = { time: point.time, estimated: gap };
      continue;
    }
    if (state === 'active' && confirmed) {
      lastIrrigation = { startedAt: since, endedAt: pending.time, durationMs: pending.time - since, minimum, estimated };
    }
    state = observed;
    confirmed = true;
    since = pending.time;
    minimum = false;
    estimated = pending.estimated;
    pending = undefined;
  }
  if (!currentKnown || !valid(pressure)) return { ...unknown, lastIrrigation };
  return { state: state ?? unknown.state, pressure, since, minimum, estimated: estimated || pending !== undefined, lastIrrigation };
}
